import { Component, ComponentDefinition, isLabeledComponentDefinition, isValueComponent } from "components/components/Component";
import { Form } from "components/form/form";
import i18n from "components/i18n/i18n";
import { Renderer } from "components/Renderer";
import { isComponentColumnDefinition } from "components/table/columns/ComponentColumn";
import sislogUserGlobals from "components/UserGlobals";
import moment from "moment";
import validate from "validate.js";
import { Table } from "../table/table";
import { initDateRangeValidator } from "./dateRangeValidator";
import {
    isDateRangeValidation,
    isDateValidation,
    isLengthValidation,
    isNestedValidation,
    isNumericValidation,
    isPresenceValidation,
    LengthValidation,
    NestedValidation,
    NumericValidation,
    RestrictionHolder,
    Validation,
} from "./Restrictions";

validate.extend(validate.validators.datetime, {
    // The value is guaranteed not to be null or undefined but otherwise it
    // could be anything.
    parse(value, options) {
        return +moment.utc(value);
    },
    // Input is a unix timestamp
    format(value, options) {
        if (value == null) {
            return value;
        }
        if (options.dateOnly ||options.dateOnly == null) {
            return new Date(value).toLocaleDateString(sislogUserGlobals.language,
                { day: "2-digit", month: "2-digit", year: "numeric", timeZone: "UTC" });

        } else {
            return new Date(value).toLocaleDateString(sislogUserGlobals.language,
                { hour: "2-digit", minute: "2-digit", second: "2-digit", timeZone: "UTC" });
        }
    },
});
initDateRangeValidator();

export function buildConstraints(restrictionList: RestrictionHolder, container: Form | Table) {
    if (restrictionList == null) {
        return;
    }
    let constraints = {};
    // tslint:disable-next-line:forin
    for (let restrictionKey in restrictionList) {
        let restriction = restrictionList[restrictionKey];
        let constraint = {};
        constraints[restrictionKey] = constraint;
        let definition = findFirstComponentDefinition(container, restrictionKey);
        for (let r of restriction.attributes ?? []) {
            if (r === "required") {
                let message;
                if (definition == null) {
                    console.error("No component for restriction " + restrictionKey);
                    continue;
                }
                if (isLabeledComponentDefinition(definition)) {
                    message = i18n.instance.t(
                        "validation.required",
                        { fieldLabelKey: definition.labelKey });
                } else {
                    message = i18n.instance.t("validation.inline.required");
                }
                // tslint:disable-next-line: no-string-literal
                constraint["presence"] = {
                    message,
                    allowEmpty: false,
                };
            }
        }
        if (!Array.isArray(restriction.validations) && restriction.validations != null) {
            continue;
        }
        for (let v of restriction.validations ?? []) {
            processValidation(constraints, definition, v, restrictionKey);
        }
    }
    return constraints;
}
function processValidation(
    constraints,
    definition: ComponentDefinition,
    validation: Validation,
    restrictionKey: string) {
    if (isLengthValidation(validation)) {
        processLength(constraints, definition, validation, restrictionKey);
    }
    if (isNumericValidation(validation)) {
        processNumeric(constraints, definition, validation, restrictionKey);
    }

    if (isNestedValidation(validation)) {
        processNested(constraints, definition, validation, restrictionKey);
    }

    if (isPresenceValidation(validation)) {
        if (constraints[restrictionKey] == null) {
            constraints[restrictionKey] = {};
        }
        constraints[restrictionKey][validation.validator] = {
            allowEmpty: false,
            message: i18n.instance.t("validation.inline.required"),
        };
    }

    if (isDateValidation(validation)) {
        if ((validation.options.earliest != null) && (validation.options.latest != null)) {
            constraints[restrictionKey][validation.validator] = {
                earliest: validation.options.earliest,
                latest: validation.options.latest,
                message: i18n.instance.t(
                    "validation.datetime.between",
                    {
                        date1: validation.options.earliest,
                        date2: validation.options.latest,
                    }),
            };
        } else {
            if (validation.options.earliest != null) {
                constraints[restrictionKey][validation.validator] = {
                    earliest: validation.options.earliest,
                    message: i18n.instance.t(
                        "validation.datetime.tooEarly",
                        { date: validation.options.earliest }),
                };
            }
            if (validation.options.latest != null) {
                constraints[restrictionKey][validation.validator] = {
                    latest: validation.options.latest,
                    message: i18n.instance.t(
                        "validation.datetime.tooLate",
                        { date: validation.options.latest }),
                };
            }
        }
    }

    if (isDateRangeValidation(validation)) {
        if ((validation.options.earliest != null) && (validation.options.latest != null)) {
            constraints[restrictionKey][validation.validator] = {
                earliest: validation.options.earliest,
                latest: validation.options.latest,
                message: i18n.instance.t("validation.daterange.between"),
            };
        } else {
            if (validation.options.earliest != null) {
                constraints[restrictionKey][validation.validator] = {
                    earliest: validation.options.earliest,
                    message: i18n.instance.t("validation.daterange.tooEarly")
                };
            }
            if (validation.options.latest != null) {
                constraints[restrictionKey][validation.validator] = {
                    latest: validation.options.latest,
                    message: i18n.instance.t("validation.daterange.tooLate")
                };
            }
        }
    }
}

function processNumeric(
    constraints,
    definition: ComponentDefinition,
    validation: NumericValidation,
    restrictionKey: string) {
    let message;
    if (validation.options.lessThan != null && validation.options.greaterThan == null) {
        message = i18n.instance.t(
            "validation.numeric.lessThan",
            { number: validation.options.lessThan });
    }
    if (validation.options.greaterThan != null && validation.options.lessThan == null) {
        message = i18n.instance.t(
            "validation.numeric.greaterThan",
            { number: validation.options.greaterThan });
    }
    if (validation.options.greaterThan != null && validation.options.lessThan != null) {
        message = i18n.instance.t(
            "validation.numeric.between",
            {
                number1: validation.options.greaterThan,
                number2: validation.options.lessThan,
            });
    }
    if (constraints[restrictionKey] == null) {
        constraints[restrictionKey] = {};
    }
    constraints[restrictionKey].numericality = {
        message,
        ...validation.options,
    };
}

function processLength(
    constraints,
    definition: ComponentDefinition,
    validation: LengthValidation,
    restrictionKey: string) {
    let tooShort;
    if (isLabeledComponentDefinition(definition)) {
        tooShort = i18n.instance.t(
            "validation.minlength",
            { fieldLabelKey: definition.labelKey });
    } else {
        tooShort = i18n.instance.t("validation.inline.minlength");
    }
    if (constraints[restrictionKey] == null) {
        constraints[restrictionKey] = {};
    }
    constraints[restrictionKey][validation.validator] = {
        tooShort,
        ...validation.options,
    };
}

function processNested(
    constraints,
    definition: ComponentDefinition,
    validation: NestedValidation,
    restrictionKey: string) {
    // tslint:disable-next-line: forin
    for (const key in constraints) {
        if (key.startsWith(restrictionKey + ".")) {
            delete constraints[key];
        }
    }

    // tslint:disable-next-line: forin
    for (const key in validation.validations) {
        let validations = validation.validations[key];
        for (const val of validations) {
            processValidation(constraints, definition, val, restrictionKey + "." + key);
        }
    }
}

function findFirstComponentDefinition(component: Component, property: string): ComponentDefinition {
    if (component instanceof Form) {
        let found = Renderer.findFirstInTree(component, (c) => {
            if (isValueComponent(c)) {
                return c.definition.property === property;
            }
            return false;
        });
        if (found != null) {
            return found.definition;
        }
    }
    if (component instanceof Table) {
        for (let column of component.dataColumns) {
            if (isComponentColumnDefinition(column)) {
                if (column.property === property) {
                    return column.component;
                }
            }
            if (column.insertComponent != null) {
                if (column.property === property) {
                    return column.insertComponent;
                }
            }
        }
    }
    return null;
}
